import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom"; 
import axios from "axios";
import Swal from "sweetalert2";
import { baseurl } from "../../../component/Baseurl";
import Sidebar from "./Sidebar";
import BreadCrumb from "./BreadCrumb";

function EditPackage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [packageData, setPackageData] = useState({
    package_name: "",
    package_description: "",
    price: "",
    banner_image: null, // Stores actual file
    banner_preview: "", // Stores preview URL
    package_details: [],
  });

  // Fetch package details when component mounts
  useEffect(() => {
    const fetchPackage = async () => {
      try {
        const response = await axios.get(`${baseurl}/GetPackageById/${id}`);
        if (response.data.package) {
          const packageInfo = response.data.package;
          setPackageData({
            ...packageInfo,
            banner_preview: packageInfo.banner_image || "", // Set existing image preview
          });
        }
      } catch (error) {
        console.error("Error fetching package:", error);
        Swal.fire("Error", "Failed to fetch package details", "error");
      }
    };

    fetchPackage();
  }, [id]);

  // Handle input change
  const handleInputChange = (e) => {
    setPackageData({ ...packageData, [e.target.name]: e.target.value });
  };

  // Handle file upload
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPackageData({
        ...packageData,
        banner_image: file,
        banner_preview: URL.createObjectURL(file), // Show preview
      });
    }
  };

  // Handle package details change
  const handleDetailChange = (index, field, value) => {
    const updatedDetails = [...packageData.package_details];
    updatedDetails[index][field] = value;
    setPackageData({ ...packageData, package_details: updatedDetails });
  };

  // Add a new key-value row
  const handleAddRow = () => {
    setPackageData({
      ...packageData,
      package_details: [...packageData.package_details, { key_name: "", value: "" }],
    });
  };

  // Remove a row
  const handleDeleteRow = (index) => {
    const updatedDetails = packageData.package_details.filter((_, i) => i !== index);
    setPackageData({ ...packageData, package_details: updatedDetails });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const formData = new FormData(); 
      formData.append("package_name", packageData.package_name);
      formData.append("package_description", packageData.package_description);
      formData.append("price", packageData.price);
      if (packageData.banner_image) {
        formData.append("bannerImage", packageData.banner_image);
      }
      formData.append("package_details", JSON.stringify(packageData.package_details));

      // Log FormData before sending
      console.log("FormData being sent:");
      for (let [key, value] of formData.entries()) {
        console.log(key, value);
      }

      await axios.put(`${baseurl}UpdatePackageNew/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      Swal.fire({
        title: "Success!",
        text: "Package updated successfully",
        icon: "success",
      }).then(() => navigate(-1));
    } catch (error) {
      console.error("Error updating package:", error);
      Swal.fire("Error", "Failed to update package", "error");
    }
  };

  return (
    <>
      <div className="App" style={{ display: "flex" }}>
        <Sidebar />
        <div className="content" style={{ flexGrow: 1, padding: "20px" }}>
          <div>
            <BreadCrumb />
            <div className="container-fluid">
              <div className="crdmain mb-2">
                <h2 className="chart-title mb-0">Edit Package</h2>

                <form onSubmit={handleSubmit}>
                  {/* Banner Image Upload */}
                  <div className="col-md-12 mb-3">
                    <label className="text-dark mb-1 labelset2 font3 text-start">
                      Package Banner Image
                    </label>
                    <input type="file" className="input1" accept="image/*" onChange={handleFileChange} />
                    
                    {/* Preview Image */}
                    {packageData.banner_preview && (
                      <div className="mt-2">
                        <img
                          src={packageData.banner_preview}
                          alt="Banner Preview"
                          className="img-fluid"
                          style={{ maxWidth: "100%", maxHeight: "300px" }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Package Name, Description, and Price */}
                  <div className="row g-1 text-start">
                    <div className="col-md-12">
                      <label className="text-dark mb-1 labelset2 font3">Package Name</label>
                      <input
                        type="text"
                        className="input1"
                        name="package_name"
                        placeholder="Enter package name"
                        value={packageData.package_name}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-md-12">
                      <label className="text-dark mb-1 labelset2 font3">Package Description</label>
                      <textarea
                        className="inputtextarea"
                        name="package_description"
                        placeholder="Enter package description"
                        rows="3"
                        value={packageData.package_description}
                        onChange={handleInputChange}
                      ></textarea>
                    </div>
                    <div className="col-md-12">
                      <label className="text-dark mb-1 labelset2 font3">Price</label>
                      <input
                        type="number"
                        className="input1"
                        name="price"
                        placeholder="Enter price"
                        value={packageData.price}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  {/* Package Details (Key-Value Pairs) */}
                  <div className="mt-3">
                    <h6 className="text-start text-dark">Package Details</h6>
                    {packageData.package_details.map((row, index) => (
                      <div key={index} className="row g-2 align-items-center mb-2">
                        <div className="col-md-12">
                          <input
                            type="text"
                            className="input1"
                            placeholder="Enter key"
                            value={row.key_name}
                            onChange={(e) => handleDetailChange(index, "key_name", e.target.value)}
                          />
                        </div>
                        <div className="col-md-12">
                          <textarea
                            className="inputtextarea"
                            placeholder="Enter value"
                            rows={4}
                            value={row.value}
                            onChange={(e) => handleDetailChange(index, "value", e.target.value)}
                          ></textarea>
                        </div>
                        <div className="col-md-12 text-end">
                          <button type="button" className="btn2" title="Delete" onClick={() => handleDeleteRow(index)}>
                            ❌ Delete
                          </button>
                        </div>
                      </div>
                    ))}

                    <div className="text-start">
                      <button type="button" className="btn4 btn-sm" onClick={handleAddRow}>
                        ➕ Add Row
                      </button>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="text-end mt-3">
                    <button type="submit" className="submit-btn">
                      Save Package
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default EditPackage;
